import React from "react";

import { Disclosure, Transition } from "@headlessui/react";
import { MinusIcon, PlusIcon } from "@heroicons/react/outline";

import clsx from "clsx";

import Heading from "components/heading";

type FAQItem = { question: string; answer: React.ReactNode };

const QuestionBlock = ({ question, answer }: FAQItem) => {
  return (
    <Disclosure>
      {({ open }) => (
        <div className="bg-gray-50 rounded-lg border border-gray-200 text-left">
          <Disclosure.Button
            className={clsx(
              "text-left py-3 px-4 w-full flex justify-between items-baseline gap-4 focus:ring-0 hover:bg-gray-100",
              open && "rounded-b-none"
            )}
          >
            <Heading tag="h3" size="h6" className="text-navy !text-base">
              {question}
            </Heading>
            <i className="relative h-4 w-4 shrink-0">
              <MinusIcon
                className={clsx(
                  "h-full w-full text-primary absolute inset-0 transition-all",
                  open ? "opacity-100" : "opacity-0"
                )}
              />
              <PlusIcon
                className={clsx(
                  "h-full w-full text-primary absolute inset-0 transition-all",
                  open ? "opacity-0 transform rotate-45" : "opacity-100"
                )}
              />
            </i>
          </Disclosure.Button>
          <Transition
            enter="transition-all duration-100 ease-out relative"
            enterFrom="-top-2 opacity-0"
            enterTo="top-0 opacity-100"
            leave="transition-all duration-75 ease-out relative"
            leaveFrom="top-0 opacity-100"
            leaveTo="-top-2 opacity-0"
          >
            <Disclosure.Panel className="text-gray-600 p-4 [font-variant-ligatures:none]">{answer}</Disclosure.Panel>
          </Transition>
        </div>
      )}
    </Disclosure>
  );
};

export const FaqBlocks = ({ items }: { items: FAQItem[] }) => {
  const middleIndex = Math.ceil(items.length / 2);
  return (
    <div className="flex flex-col gap-4 lg:grid lg:grid-cols-2 lg:gap-6">
      <div className="space-y-4">
        {items.slice(0, middleIndex).map(faq => (
          <QuestionBlock key={faq.question} {...faq} />
        ))}
      </div>
      <div className="space-y-4">
        {items.slice(middleIndex).map(faq => (
          <QuestionBlock key={faq.question} {...faq} />
        ))}
      </div>
    </div>
  );
};
